import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";

interface ChannelSharesDialogProps {
  open: boolean;
  onClose: () => void;
  // Incoming requests (user is recipient)
  incomingRequests: any[];
  shareActionLoading: string | null;
  onAccept: (token: string) => void;
  onDecline: (token: string) => void;
  onRefreshIncoming: () => void;
  // Outgoing shares across all channels
  onRefreshShares: () => void;
  channelShares: any[];
  cancellingShareId: number | null;
  onCancelShare: (shareId: number) => void;
}

const ChannelSharesDialog = ({
  open,
  onClose,
  incomingRequests,
  shareActionLoading,
  onAccept,
  onDecline,
  onRefreshIncoming,
  onRefreshShares,
  channelShares,
  cancellingShareId,
  onCancelShare,
}: ChannelSharesDialogProps) => {
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (open) {
      onRefreshIncoming();
      onRefreshShares();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={{ color: "#1a1a1a !important" }} variant="h6">
            Channel Sharing
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab
            label={
              incomingRequests.length > 0
                ? `Requests (${incomingRequests.length})`
                : "Requests"
            }
          />
          <Tab label="Shared With" />
        </Tabs>

        {/* Tab 0 — Incoming requests */}
        {tab === 0 && (
          <Box>
            {incomingRequests.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No pending share requests
              </Typography>
            ) : (
              <Box display="flex" flexDirection="column" gap={2}>
                {incomingRequests.map((share: any) => (
                  <Box
                    key={share.ID}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="body2" mb={1}>
                      <strong>{share.OwnerName || share.OwnerEmail}</strong>{" "}
                      wants to share{" "}
                      {share.ChannelName ? (
                        <strong>"{share.ChannelName}"</strong>
                      ) : (
                        "a channel"
                      )}{" "}
                      with you
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={!!shareActionLoading}
                        onClick={() => onAccept(share.Token)}
                      >
                        {shareActionLoading === `accept-${share.Token}` ? (
                          <CircularProgress size={16} />
                        ) : (
                          "Accept"
                        )}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={!!shareActionLoading}
                        onClick={() => onDecline(share.Token)}
                      >
                        {shareActionLoading === `decline-${share.Token}` ? (
                          <CircularProgress size={16} />
                        ) : (
                          "Decline"
                        )}
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Tab 1 — All outgoing shares (table) */}
        {tab === 1 && (
          <Box>
            {channelShares.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                You have not shared any channels yet.
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Channel</th>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Recipient</th>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Status</th>
                      <th style={{ textAlign: "center", padding: "8px 12px", fontWeight: 600 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelShares.map((share: any) => (
                      <tr key={share.ID} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "8px 12px" }}>{share.ChannelName || `#${share.ChannelID}`}</td>
                        <td style={{ padding: "8px 12px" }}>{share.RecipientEmail}</td>
                        <td style={{ padding: "8px 12px" }}>
                          <Chip
                            label={share.Status}
                            size="small"
                            color={
                              share.Status === "accepted"
                                ? "success"
                                : share.Status === "declined"
                                ? "error"
                                : "default"
                            }
                            variant="outlined"
                          />
                        </td>
                        <td style={{ padding: "8px 12px", textAlign: "center" }}>
                          {share.Status === "pending" && (
                            <IconButton
                              size="small"
                              onClick={() => onCancelShare(share.ID)}
                              disabled={cancellingShareId === share.ID}
                            >
                              {cancellingShareId === share.ID ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Close fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChannelSharesDialog;
